/**
 * 개정사항 표 관련 유틸리티 함수들
 */
import { insertHTMLAtEnd, scrollElementIntoView, setupGlobalTableHandlers, updateFormContent } from './domUtils';



/**
 * 개정사항 표를 제거하는 함수
 * 
 * @param content - 원본 콘텐츠
 * @returns {string} 개정사항 표가 제거된 콘텐츠
 */
export const removeAmendmentTable = (content: string): string => {
  if (!content) return content;
  
  // 개정사항 표 패턴을 찾아서 제거
  // <div class="amendment-table"> 전체를 제거
  const amendmentTablePattern = /<div class="amendment-table">[\s\S]*?<\/div>\s*<\/div>/g;
  
  return content.replace(amendmentTablePattern, '');
};

// 개정사항 표 HTML 생성
export const generateAmendmentTableHTML = (
  amendmentTitle: string = '개정사항',
  beforeLabel: string = '변경 전',
  afterLabel: string = '변경 후',
  addRowText: string = '행 추가',
  deleteRowText: string = '행 삭제'
): string => {
  return `
    <div class="amendment-table">
      <p class="amendment-title">${amendmentTitle}</p>
      <hr>
      <table>
        <thead>
          <tr>
            <th>${beforeLabel}</th>
            <th>${afterLabel}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td contenteditable="true"></td>
            <td contenteditable="true"></td>
          </tr>
          <tr>
            <td contenteditable="true"></td>
            <td contenteditable="true"></td>
          </tr>
        </tbody>
      </table>
      <div class="table-controls">
        <div class="add-row-btn">${addRowText}</div>
        <div class="delete-row-btn">${deleteRowText}</div>
      </div>
    </div>
  `;
};

// 개정사항 표 삽입 함수
export const insertAmendmentTable = (
  contentRef: React.RefObject<HTMLDivElement>,
  form: any,
  amendmentTitle?: string,
  beforeLabel?: string,
  afterLabel?: string,
  addRowText?: string,
  deleteRowText?: string
): void => {
  if (!contentRef.current) return;
  
  const amendmentTableHTML = generateAmendmentTableHTML(
    amendmentTitle,
    beforeLabel,
    afterLabel,
    addRowText,
    deleteRowText
  );
  
  // 항상 내용 텍스트 맨 끝에 삽입
  insertHTMLAtEnd(contentRef, amendmentTableHTML);
  
  // 폼 값 업데이트
  updateFormContent(form, contentRef);
  
  // 표가 제대로 삽입되었는지 확인하고 필요시 스크롤 조정
  setTimeout(() => {
    if (contentRef.current) {
      const amendmentTable = contentRef.current.querySelector('.amendment-table');
      scrollElementIntoView(amendmentTable);
    }
  }, 100);
};

// 안전장치: 전역 함수 정의 (onclick 속성이 남아있을 경우를 대비)
setupGlobalTableHandlers();



// 개정사항 표 CSS 스타일
export const amendmentTableStyles = `
  /* 개정사항 표 스타일 */
  .amendment-table {
    margin-top: 25px;
    margin-bottom: 25px;
    width: 100%;
    max-width: 100%;
    overflow-x: auto;
  }
  
  .amendment-title {
    text-align: center;
    font-size: 18px;
    margin-bottom: 15px;
    font-weight: bold;
    color: #2c3e50;
    padding: 10px 0;
  }
  
  .amendment-table hr {
    border: none;
    height: 2px;
    background-color: #1890ff;
    margin: 15px 0 20px 0;
    display: block;
  }
  
  .amendment-table table {
    width: 100%;
    max-width: 100%;
    border-collapse: collapse;
    border: 1px solid #ddd;
    margin-bottom: 0;
    table-layout: fixed;
    word-wrap: break-word;
    overflow-wrap: break-word;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  
  .amendment-table th {
    background-color: #f5f5f5;
    font-weight: bold;
    text-align: center;
    padding: 16px;
    border: 1px solid #ddd;
    width: 50%;
    word-wrap: break-word;
    overflow-wrap: break-word;
    height: 50px;
    vertical-align: middle;
    font-size: 14px;
  }
  
  .amendment-table td {
    border: 1px solid #ddd;
    padding: 16px;
    text-align: left;
    vertical-align: top;
    min-height: 60px;
    word-wrap: break-word;
    overflow-wrap: break-word;
    max-width: 0;
    line-height: 1.5;
    font-size: 16px;
    white-space: pre-wrap;
  }
  
  .amendment-table td[contenteditable="true"] {
    cursor: text;
  }
  
  .amendment-table td[contenteditable="true"]:focus {
    outline: 2px solid #1890ff;
    background-color: #f0f8ff;
    box-shadow: inset 0 0 0 1px #1890ff;
  }
  
  .table-controls {
    margin-top: 12px;
    text-align: center;
    padding: 12px;
    background-color: #f8f9fa;
    border-radius: 6px;
    border: 1px solid #e8e8e8;
  }
  
  .add-row-btn {
    margin: 4px;
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 500;
    background-color: #52c41a;
    color: white;
    outline: none;
    user-select: none;
    pointer-events: auto;
    display: inline-block;
    text-align: center;
    transition: all 0.2s;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
  
  .add-row-btn:hover {
    background-color: #389e0d;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }
  
  .add-row-btn:active {
    background-color: #237804;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }
  
  .delete-row-btn {
    margin: 4px;
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 500;
    background-color: #ff4d4f;
    color: white;
    outline: none;
    user-select: none;
    pointer-events: auto;
    display: inline-block;
    text-align: center;
    transition: all 0.2s;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
  
  .delete-row-btn:hover {
    background-color: #cf1322;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }
  
  .delete-row-btn:active {
    background-color: #a8071a;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }
  

`; 